import React, { FC, useCallback, useEffect, useState } from 'react';
import { message, Spin, Space, Row, Col, Button, Typography } from 'antd';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AmountField, Form, FxRateField, TextField } from '~/components';
import AmountInput from '~/components/inputs/AmountInput';
import OatCard from './OatCard';
import { Consent, Transaction } from '~/types';
import c from '~/utils/constants';
import { getAmountText } from '~/components/fields/AmountField';
import { createOatConsent, makeOatTransfer, updateOatConsent } from '~/api/oat';
import { AxiosResponse } from 'axios';
import theme from '~/theme';
import { OatConsentData } from './OatConsentData';
import { isDisplayedEntryCategory } from '~/utils/transaction';

interface Props {
  setMainState: React.Dispatch<React.SetStateAction<OatConsentData>>;
  mainState: OatConsentData;
  className?: string;
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[] | undefined>>;
}

const AmountAndConsent: FC<Props> = (props) => {
  const { mainState, setMainState, className, setTransactions } = props;
  const [form] = Form.useForm();

  const [consent, setConsent] = useState<Consent[]>({
    id: '',
  } as any);
  const [loading, setLoading] = useState(false);
  const [loadingTransfer, setLoadingTransfer] = useState(false);

  const disabled = !mainState.senderAccount || !mainState.recipientAccount;

  const [t] = useTranslation('oat');
  const tt = (key: string): string => t(`transactions:${key}`);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleConsent = useCallback(
    debounce(async (data: OatConsentData, consentId?: string) => {
      try {
        const request = {
          senderAccountId: data.senderAccount!.id,
          recipientAccountId: data.recipientAccount!.id,
          amount: data.amount,
          comment: data.comment,
        };
        const res: AxiosResponse<Consent[]> = consentId
          ? await updateOatConsent(consentId, request)
          : await createOatConsent(request);
        setConsent(res.data);
      } catch (error: any) {
        console.log(`error:`, error);
        switch (error.response?.data?.code) {
          case 'LIMIT_REACHED': {
            console.log(`limit reached`);
            form.setFields([{ name: ['amount', 'value'], errors: [t('LIMIT_REACHED')] }]);
            break;
          }
          case 'NOT_SUFFICIENT_FUNDS': {
            form.setFields([{ name: ['amount', 'value'], errors: [t('NOT_SUFFICIENT_FUNDS')] }]);
            break;
          }
          default:
            void message.error(error.response?.data?.message);
        }
        setConsent(() => []);
      }
      setLoading(false);
    }, 1000),
    []
  );

  const consentId = consent?.[0]?.id;

  useEffect(() => {
    const { amount } = mainState;
    if (!disabled && (amount.value || 0) > 0 && amount.currency) {
      setLoading(true);
      void handleConsent(mainState, consentId);
    } else {
      setConsent(() => []);
    }
  }, [mainState, consentId, handleConsent, disabled]);

  const handleTransfer = async () => {
    setLoadingTransfer(true);
    try {
      const res: AxiosResponse<Transaction[]> = await makeOatTransfer(consent?.[0]?.id);
      setTransactions(res.data);
    } catch (error: any) {
      void message.error(error.response?.data?.message);
    }
    setLoadingTransfer(false);
  };

  const senderExchangeRate = consent?.[0]?.details?.currencyConversion?.sender?.rate;
  const recipientExchangeRate = consent?.[1]?.details?.currencyConversion?.recipient?.rate;

  const creditAmount = consent?.[1]?.totalAmount;

  return (
    <div className={className}>
      <Typography.Title level={4}>{t('translation:amount')}</Typography.Title>
      <OatCard borderBottomNoRadius={!!consent[0]?.totalAmount || loading}>
        <Form
          form={form}
          name="amount"
          onValuesChange={(_, data: any) => setMainState((prev) => ({ ...prev, ...data }))}
        >
          <Form.Item name="amount">
            <AmountInput
              form={form}
              min={0}
              disabled={disabled}
              currencyFilter={(currency: string) =>
                mainState.senderAccount?.balances?.[0]?.amount?.currency === currency ||
                mainState.recipientAccount?.balances?.[0]?.amount?.currency === currency
              }
            />
          </Form.Item>
        </Form>
      </OatCard>
      {loading && (
        <div className="AmountAndConsent-Details AmountAndConsent-Details__loading">
          <Spin />
        </div>
      )}
      {consent[0]?.totalAmount && !loading && (
        <Space direction="vertical" className="full-width" size={16}>
          <div className="AmountAndConsent-Details">
            <Row>
              <Col span={12}>
                <Space direction="vertical" size={16}>
                  <TextField label={t('Credit amount')}>
                    <AmountField amount={creditAmount} />
                  </TextField>
                  {senderExchangeRate && (
                    <>
                      <TextField label={tt('exchangeRate')}>
                        <FxRateField
                          baseCurrency={consent[0]?.baseAmount.currency as string}
                          quoteCurrency={consent[0]?.totalAmount.currency as string}
                          rate={senderExchangeRate}
                        />
                      </TextField>
                    </>
                  )}
                  {recipientExchangeRate && (
                    <>
                      <TextField label={tt('exchangeRate')}>
                        <FxRateField
                          baseCurrency={consent[1]?.baseAmount.currency as string}
                          quoteCurrency={consent[1]?.totalAmount.currency as string}
                          rate={recipientExchangeRate}
                        />
                      </TextField>
                    </>
                  )}
                  {consent[0]?.entries
                    ?.filter((item) => isDisplayedEntryCategory(item.category))
                    ?.map((item, index) => {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <TextField key={index} label={item.description || tt(item.category)}>
                          <AmountField amount={item.amount} abs />
                        </TextField>
                      );
                    })}
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size={16}>
                  <TextField label={tt('totalAmount')}>
                    <AmountField
                      className="AmountAndConsent-TotalAmount"
                      amount={consent[0]?.totalAmount}
                      abs
                    />
                  </TextField>
                </Space>
              </Col>
            </Row>
          </div>
          <Button
            type="primary"
            className="AmountAndConsent-SendButton"
            onClick={handleTransfer}
            loading={loadingTransfer}
          >
            {t('transfer', { amount: getAmountText(consent[0]?.baseAmount, true) })}
          </Button>
        </Space>
      )}
    </div>
  );
};

export default styled(AmountAndConsent)`
  .ant-form-item {
    margin-bottom: 0;
  }
  .AmountAndConsent-Details {
    background: #ffffff;
    padding: 12px 16px;
    border: 1px solid #e6e6e6;
    border-bottom-left-radius: ${c.borderRadiusBase}px;
    border-bottom-right-radius: ${c.borderRadiusBase}px;
    border-top: none;
  }
  .AmountAndConsent-Details__loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .AmountAndConsent-SendButton {
    width: 100%;
    font-size: 18px;
    height: 40px;
    font-weight: 600;
  }
  .AmountAndConsent-TotalAmount {
    color: ${theme.primaryColor};
  }
`;
